﻿import { Spin } from "@/components";
import { FieldNames, FormType } from "@/enums";
import { IProjectRoleEntityUsage } from "@/interfaces/Project/Roles/IUpdateProjectRoleActiveStateResult";
import { useFormPropertyValueLookupProvider } from "@/providers";
import { useUpdateProjectRoleActiveStateMutation } from "@/services/projectRole";
import { Button, Modal } from "antd";
import { ReactNode, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

interface IProps {
  projectRoleId: string;
  onModalClose: (disabled: boolean) => void;
  isOpen: boolean;
}

export const ConfirmDisableProjectRoleModal = (props: IProps): ReactNode => {
  const [updateProjectRoleActiveState, { isLoading }] =
    useUpdateProjectRoleActiveStateMutation();
  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();
  const [activeUsages, setActiveUsages] = useState<IProjectRoleEntityUsage[]>(
    []
  );
  const intl = useIntl();

  // Hate using TS Ignore, but the result from the mutation isn't typed correctly - or I'm doing something wrong
  const attemptToDisableProjectRole = async (removeAndDeactivate: boolean) => {
    const res = await updateProjectRoleActiveState({
      id: props.projectRoleId,
      isActive: false,
      removeAndDeactivate: removeAndDeactivate,
    });

    // @ts-ignore
    const data = res.data;
    // @ts-ignore
    const error = res.error;

    if (error) {
      toast.error(intl.formatMessage({ id: "projectRoles.saveRoleError" }));
      props.onModalClose(false);
      return;
    }

    if (data) {
      if (!data.isActive) {
        props.onModalClose(true);
      } else {
        setActiveUsages(data.activeUsages as IProjectRoleEntityUsage[]);
        return;
      }

      setActiveUsages([]);
      toast.success(intl.formatMessage({ id: "projectRoles.saveRoleSuccess" }));
      return;
    }

    props.onModalClose(false);
  };

  const hasErrors = (): boolean => activeUsages.length > 0;
  const hasBlockingErrors = (): boolean =>
    activeUsages
      .flatMap((usages) => usages.childEntityUsages)
      .flatMap((childUsages) => childUsages.isOnlyApproverInWorkflow)
      .some((x) => x) ||
    activeUsages
      .flatMap((usages) => usages.childEntityUsages)
      .flatMap((childUsages) => childUsages.isOnlyRequiredMilestoneApprover)
      .some((x) => x);

  const showVerificationModal = [
    <Button
      data-test-id="Avc7VYlUV0bmArERI_uQ0"
      key="cancel"
      onClick={() => {
        setActiveUsages([]);
        props.onModalClose(false);
      }}
    >
      {intl.formatMessage({ id: "general.cancel" })}
    </Button>,
    <Button
      data-test-id="t73Hrto-N5pTXkfCwP7FF"
      key="Yes"
      type="primary"
      onClick={() => attemptToDisableProjectRole(false)}
    >
      {intl.formatMessage({ id: "general.yes" })}
    </Button>,
  ];

  const showErrorModal = [
    <Button
      data-test-id="RyDPiWsky2ZT9gppnaehr"
      key="cancel"
      onClick={() => {
        setActiveUsages([]);
        props.onModalClose(false);
      }}
    >
      {intl.formatMessage({ id: "general.cancel" })}
    </Button>,
    <Button
      data-test-id="VAKEE2zPj2HbBo35CGIkx"
      key="remove and de-activate"
      type="primary"
      onClick={() => attemptToDisableProjectRole(true)}
    >
      {intl.formatMessage({ id: "general.yes" })}
    </Button>,
  ];

  const showBlockingErrorModal = (
    <Button
      data-test-id="HymjXJ40_xxJ-KOIskQbS"
      key="cancel"
      onClick={() => {
        setActiveUsages([]);
        props.onModalClose(false);
      }}
    >
      {intl.formatMessage({ id: "general.cancel" })}
    </Button>
  );

  return (
    <Modal
      open={props.isOpen}
      okButtonProps={{
        loading: isLoading,
        disabled: hasErrors(),
      }}
      cancelButtonProps={{ loading: isLoading }}
      onCancel={() => {
        setActiveUsages([]);
        props.onModalClose(false);
      }}
      cancelText={!hasErrors() ? "No" : "Close"}
      closable={true}
      title={intl.formatMessage({ id: "projectRoles.disableRoleModalTitle" })}
      footer={
        !hasErrors() && !hasBlockingErrors()
          ? showVerificationModal
          : hasErrors() && !hasBlockingErrors()
          ? showErrorModal
          : showBlockingErrorModal
      }
    >
      {!isLoading ? (
        <div>
          {hasErrors() ? (
            <div>
              <span>
                {intl.formatMessage({ id: "projectRoles.roleHasActiveUsages" })}
                {hasBlockingErrors()
                  ? " " +
                    intl.formatMessage({
                      id: "projectRoles.roleHasActiveUsagesWithBlockingErrors",
                    })
                  : ""}
              </span>
              <ul>
                {activeUsages.map((usage, index) => (
                  <li key={index}>
                    {usage.containerType.match("(?:Milestone)") !== null
                      ? getPropertyValueDisplay(
                          FormType.Intake,
                          FieldNames.ProjectType,
                          usage.projectTypeId
                        )
                      : usage.projectTypeId}{" "}
                    - {usage.containerType}
                    <ul>
                      {usage.childEntityUsages.map((childUsage, childIndex) => (
                        <li
                          key={childIndex}
                          className={
                            childUsage.isOnlyApproverInWorkflow ||
                            childUsage.isOnlyRequiredMilestoneApprover
                              ? "text-red-500"
                              : ""
                          }
                        >
                          {childUsage.entityName}{" "}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
              {!hasBlockingErrors() && (
                <span>
                  {intl.formatMessage({
                    id: "projectRoles.removeAndDeactivatePrompt",
                  })}
                </span>
              )}
            </div>
          ) : (
            <span>
              {intl.formatMessage({
                id: "projectRoles.disableRoleVerificationWarning",
              })}
            </span>
          )}
        </div>
      ) : (
        <Spin loading={isLoading} />
      )}
    </Modal>
  );
};
